// src/components/Projects/RunningPaceCalculator/index.tsx
import React, { useState } from 'react';
import { Col, Container, Form, Row, Table } from 'react-bootstrap';
import './style.css';

type Unit = 'min/mi' | 'min/km';

interface Distance {
    label: string;
    miles?: number;
    km?: number;
    isRace?: boolean;
}

const formatTime = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    const seconds = Math.round((totalMinutes % 1) * 60);

    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const formatPace = (minutes: number, seconds: number, unit: Unit): string => {
    return `${minutes}:${seconds.toString().padStart(2, '0')}/${unit === 'min/mi' ? 'mi' : 'km'}`;
};

const milesDistances: Distance[] = [
    { label: '1 mi', miles: 1 },
    { label: '2 mi', miles: 2 },
    { label: '3 mi', miles: 3 },
    { label: '5k', miles: 3.10686, isRace: true },
    { label: '4 mi', miles: 4 },
    { label: '5 mi', miles: 5 },
    { label: '6 mi', miles: 6 },
    { label: '10k', miles: 6.21371, isRace: true },
    { label: '7 mi', miles: 7 },
    { label: '8 mi', miles: 8 },
    { label: '9 mi', miles: 9 },
    { label: '10 mi', miles: 10 },
    { label: '11 mi', miles: 11 },
    { label: '12 mi', miles: 12 },
    { label: '13 mi', miles: 13 },
    { label: '1/2 Marathon', miles: 13.1, isRace: true },
    { label: '14 mi', miles: 14 },
    { label: '15 mi', miles: 15 },
    { label: '16 mi', miles: 16 },
    { label: '17 mi', miles: 17 },
    { label: '18 mi', miles: 18 },
    { label: '19 mi', miles: 19 },
    { label: '20 mi', miles: 20 },
    { label: '21 mi', miles: 21 },
    { label: '22 mi', miles: 22 },
    { label: '23 mi', miles: 23 },
    { label: '24 mi', miles: 24 },
    { label: '25 mi', miles: 25 },
    { label: '26 mi', miles: 26 },
    { label: 'Marathon', miles: 26.2, isRace: true },
    { label: '27 mi', miles: 27 },
    { label: '28 mi', miles: 28 },
    { label: '29 mi', miles: 29 },
    { label: '30 mi', miles: 30 },
    { label: '31 mi', miles: 31 },
    { label: '50k', miles: 31.0686, isRace: true },
];

const kmDistances: Distance[] = [
    { label: '1 km', km: 1 },
    { label: '2 km', km: 2 },
    { label: '3 km', km: 3 },
    { label: '4 km', km: 4 },
    { label: '5k', km: 5, isRace: true },
    { label: '6 km', km: 6 },
    { label: '7 km', km: 7 },
    { label: '8 km', km: 8 },
    { label: '9 km', km: 9 },
    { label: '10k', km: 10, isRace: true },
    { label: '11 km', km: 11 },
    { label: '12 km', km: 12 },
    { label: '13 km', km: 13 },
    { label: '14 km', km: 14 },
    { label: '15 km', km: 15 },
    { label: '16 km', km: 16 },
    { label: '10 mi', km: 16.0934 },
    { label: '17 km', km: 17 },
    { label: '18 km', km: 18 },
    { label: '19 km', km: 19 },
    { label: '20 km', km: 20 },
    { label: '21 km', km: 21 },
    { label: '1/2 Marathon', km: 21.0975, isRace: true },
    { label: '22 km', km: 22 },
    { label: '23 km', km: 23 },
    { label: '24 km', km: 24 },
    { label: '25 km', km: 25 },
    { label: '26 km', km: 26 },
    { label: '27 km', km: 27 },
    { label: '28 km', km: 28 },
    { label: '29 km', km: 29 },
    { label: '30 km', km: 30 },
    { label: '31 km', km: 31 },
    { label: '32 km', km: 32 },
    { label: '33 km', km: 33 },
    { label: '34 km', km: 34 },
    { label: '35 km', km: 35 },
    { label: '36 km', km: 36 },
    { label: '37 km', km: 37 },
    { label: '38 km', km: 38 },
    { label: '39 km', km: 39 },
    { label: '40 km', km: 40 },
    { label: '41 km', km: 41 },
    { label: '42 km', km: 42 },
    { label: 'Marathon', km: 42.195, isRace: true },
    { label: '43 km', km: 43 },
    { label: '44 km', km: 44 },
    { label: '45 km', km: 45 },
    { label: '46 km', km: 46 },
    { label: '47 km', km: 47 },
    { label: '48 km', km: 48 },
    { label: '49 km', km: 49 },
    { label: '50k', km: 50, isRace: true },
];

const RunningPaceCalculator: React.FC = () => {
    const [minutes, setMinutes] = useState<number>(9);
    const [seconds, setSeconds] = useState<number>(0);
    const [unit, setUnit] = useState<Unit>('min/mi');

    const calculateFinishTime = (distance: Distance, paceMinutes: number, paceSeconds: number): string => {
        const paceInMinutes = paceMinutes + paceSeconds / 60;
        const distanceValue = unit === 'min/mi' ? (distance.miles || 0) : (distance.km || 0);
        const totalMinutes = paceInMinutes * distanceValue;
        return formatTime(totalMinutes);
    };

    const getPaceWithOffset = (offsetSeconds: number): { minutes: number; seconds: number } => {
        const totalSeconds = minutes * 60 + seconds + offsetSeconds;
        return {
            minutes: Math.floor(totalSeconds / 60),
            seconds: totalSeconds % 60,
        };
    };

    const distances = unit === 'min/mi' ? milesDistances : kmDistances;

    const pace1 = getPaceWithOffset(-10);
    const pace2 = getPaceWithOffset(-5);
    const pace3 = { minutes, seconds };
    const pace4 = getPaceWithOffset(5);
    const pace5 = getPaceWithOffset(10);

    return (
        <Container fluid className="running-pace-calculator">
            <h2 className="calculator-title">Running Pace Calculator</h2>
            <Row>
                <Col md={3} className="pace-input-column">
                    <h4>Expected Pace</h4>
                    <Form>
                        <Form.Group className="mb-3 pace-input-row">
                            <Form.Label>Minutes</Form.Label>
                            <Form.Select
                                className="form-select"
                                value={minutes}
                                onChange={(e) => setMinutes(parseInt(e.target.value))}
                            >
                                {Array.from({ length: 18 }, (_, i) => i + 3).map((min) => (
                                    <option key={min} value={min}>
                                        {min} min
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3 pace-input-row">
                            <Form.Label>Seconds</Form.Label>
                            <Form.Select
                                className="form-select"
                                value={seconds}
                                onChange={(e) => setSeconds(parseInt(e.target.value))}
                            >
                                {Array.from({ length: 60 }, (_, i) => i).map((sec) => (
                                    <option key={sec} value={sec}>
                                        {sec} sec
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group>
                            <Form.Check
                                type="radio"
                                id="unit-mi"
                                label="min/mi"
                                checked={unit === 'min/mi'}
                                onChange={() => setUnit('min/mi')}
                            />
                            <Form.Check
                                type="radio"
                                id="unit-km"
                                label="min/km"
                                checked={unit === 'min/km'}
                                onChange={() => setUnit('min/km')}
                            />
                        </Form.Group>
                    </Form>
                </Col>

                <Col md={9} className="pace-table-column">
                    <div className="table-header-container">
                        <h4>Estimated Finish Time</h4>
                    </div>
                    <div className="table-container">
                        <Table bordered className="pace-table">
                            <thead>
                                <tr>
                                    <th>Distance</th>
                                    <th>{formatPace(pace1.minutes, pace1.seconds, unit)}</th>
                                    <th>{formatPace(pace2.minutes, pace2.seconds, unit)}</th>
                                    <th>{formatPace(pace3.minutes, pace3.seconds, unit)}</th>
                                    <th>{formatPace(pace4.minutes, pace4.seconds, unit)}</th>
                                    <th>{formatPace(pace5.minutes, pace5.seconds, unit)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {distances.map((distance, index) => (
                                    <tr key={index} className={distance.isRace ? 'race-row' : ''}>
                                        <td>{distance.label}</td>
                                        <td>{calculateFinishTime(distance, pace1.minutes, pace1.seconds)}</td>
                                        <td>{calculateFinishTime(distance, pace2.minutes, pace2.seconds)}</td>
                                        <td>{calculateFinishTime(distance, pace3.minutes, pace3.seconds)}</td>
                                        <td>{calculateFinishTime(distance, pace4.minutes, pace4.seconds)}</td>
                                        <td>{calculateFinishTime(distance, pace5.minutes, pace5.seconds)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default RunningPaceCalculator;
